import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  ButtonGroup,
} from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import Card from "components/Card/Card.js";
import AuthContext from "connections/auth/AuthProvider.js";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import SearchIcon from "@material-ui/icons/Search";
import CardChannelTransparency from "./CardChannelTransparency.js";
import CardChainMonitoring from "./CardChainMonitoring.js";
import ModalChainMonitoring from "./ModalChainMonitoring.js";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  panelCard: {
    marginBottom: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  panelTitle: {
    textAlign: "center",
    padding: "8px 16px",
    fontWeight: 400,
    fontSize: "0.875rem",
    backgroundColor: "#FAFAFA",
    color: (props) => props.clientColor || "#1976d2",
    borderRadius: "5px 5px 0 0",
    border: `1px solid ${grayColor[11]}`,
    borderBottom: (props) => `1px solid ${props.clientColor || "#1976d2"}`,
    textTransform: "capitalize",
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    flex: "0 0 auto",
  },
  panelTitleText: {
    fontWeight: 400,
    textAlign: "center",
  },
  panelIcon: {
    fontSize: "1rem",
  },
  panelBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflow: "hidden",
  },
  cardSection: {
    height: "calc(50% - 8px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#fff",
    borderRadius: "4px",
  },
  cardTitle: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#424242",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #e0e0e0",
    flex: "0 0 auto",
  },
  cardTitleWithButton: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#424242",
    padding: theme.spacing(1, 0),
    paddingRight: theme.spacing(1),
    borderBottom: "1px solid #e0e0e0",
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  detailsButton: {
    padding: 0,
    minWidth: "auto",
    color: (props) => props.clientColor || "#1976d2",
    border: `1px solid ${(props) => props.clientColor || "#1976d2"}`,
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      backgroundColor: (props) => `${props.clientColor || "#1976d2"}15`,
    },
  },
  detailsIcon: {
    fontSize: "1.25rem",
  },
  cardContent: {
    flex: 1,
    overflow: "auto",
  },
  // Styles for view mode toggle button
  viewModeButtonGroup: {
    marginLeft: "auto",
  },
  viewModeButton: {
    padding: "1px 6px",
    fontSize: "0.6rem",
    fontWeight: 500,
    textTransform: "none",
    minWidth: "45px",
    border: (props) => `1px solid ${props.clientColor || "#1976d2"}`,
    color: (props) => props.clientColor || "#1976d2",
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: (props) => `${props.clientColor || "#1976d2"}15`,
    },
  },
  viewModeButtonSelected: {
    backgroundColor: (props) => `${props.clientColor || "#1976d2"} !important`,
    color: "white !important",
    "&:hover": {
      backgroundColor: (props) =>
        `${props.clientColor || "#1976d2"} !important`,
    },
  },
}));

export default function ChannelsPanel({ programId, filters }) {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({ clientColor: userDetails.implementation_color });
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  // Estado do modo de visualização para Channel Transparency: 'channel' ou 'client'
  const [viewMode, setViewMode] = useState("client");

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleViewModeChange = (mode) => {
    if (mode !== viewMode) {
      setViewMode(mode);
    }
  };

  return (
    <Card className={classes.panelCard}>
      <Box className={classes.panelTitle}>
        <LocalShippingIcon className={classes.panelIcon} />
        <Typography className={classes.panelTitleText}>channels</Typography>
      </Box>

      <Box className={classes.panelBody}>
        {/* Card 1: Channel Transparency */}
        <Box className={classes.cardSection}>
          <Box className={classes.cardTitleWithButton}>
            <Typography style={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {t("rgm.executive.channels.channel_transparency.title")}
            </Typography>
            <ButtonGroup size="small" className={classes.viewModeButtonGroup}>
              <Button
                className={`${classes.viewModeButton} ${viewMode === "channel" ? classes.viewModeButtonSelected : ""}`}
                onClick={() => handleViewModeChange("channel")}
              >
                Channel
              </Button>
              <Button
                className={`${classes.viewModeButton} ${viewMode === "client" ? classes.viewModeButtonSelected : ""}`}
                onClick={() => handleViewModeChange("client")}
              >
                Client
              </Button>
            </ButtonGroup>
          </Box>
          <Box className={classes.cardContent}>
            <CardChannelTransparency
              programId={programId}
              filters={filters}
              viewMode={viewMode}
            />
          </Box>
        </Box>

        {/* Card 2: Chain Monitoring */}
        <Box className={classes.cardSection}>
          <Box className={classes.cardTitleWithButton}>
            <Typography style={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {t("rgm.executive.channels.chain_monitoring.title")}
            </Typography>
            <Tooltip
              title={t("rgm.executive.channels.chain_monitoring.view_details")}
            >
              <IconButton
                onClick={handleOpenModal}
                className={classes.detailsButton}
                size="small"
              >
                <SearchIcon className={classes.detailsIcon} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className={classes.cardContent}>
            <CardChainMonitoring programId={programId} filters={filters} />
          </Box>
        </Box>
      </Box>

      {/* Modal for Chain Monitoring Details */}
      <ModalChainMonitoring
        open={modalOpen}
        onClose={handleCloseModal}
        programId={programId}
      />
    </Card>
  );
}

ChannelsPanel.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
};
